import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Carrier type
export interface Carrier {
  id?: string;
  name: string;
  email?: string;
  contactPerson?: string;
  phone?: string;
}

// Lane type
export interface Lane {
  id?: string;
  origin: string;
  destination: string;
  isActive: boolean;
  price: number; // Price for this lane
  carrierId?: string; // Associated carrier ID
  carrierName?: string; // Associated carrier name
  lumperFeeRate?: number; // Specific lumper fee rate for this lane
  createdAt: Date;
}

// Settings type
export interface Settings {
  id?: string;
  userId: string;
  scanInterval: number; // in hours
  lanes: string[]; // references to lane IDs
  createdAt: Date;
  updatedAt: Date;
}

// Receipt type
export interface Receipt {
  id?: string;
  userId: string;
  carrierId: string;
  carrierName: string;
  laneId?: string;
  origin?: string;
  destination?: string;
  date: Date;
  bolNumber: string;
  shipmentNumber?: string;
  amount: number;
  status: 'pending' | 'paid' | 'cancelled';
  emailId?: string;
  pdfFilename?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Convert object to Firestore format (remove undefined fields)
const toFirestoreData = <T extends object>(data: T): Record<string, any> => {
  const result: Record<string, any> = {};
  
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && key !== 'id') {
      if (value instanceof Date) {
        result[key] = Timestamp.fromDate(value);
      } else {
        result[key] = value;
      }
    }
  });
  
  return result;
};

// Save a carrier
export const saveCarrier = async (carrier: Carrier) => {
  try {
    const carrierData = toFirestoreData(carrier);
    
    if (carrier.id) {
      // Update existing carrier
      await updateDoc(doc(db, 'carriers', carrier.id), carrierData);
      return carrier.id;
    } else {
      // Add new carrier
      const docRef = await addDoc(collection(db, 'carriers'), carrierData);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving carrier: ', error);
    throw error;
  }
};

// Get all carriers
export const getCarriers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'carriers'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as Carrier);
  } catch (error) {
    console.error('Error fetching carriers: ', error);
    throw error;
  }
};

// Get a carrier by ID
export const getCarrierById = async (carrierId: string): Promise<Carrier | null> => {
  try {
    const docRef = doc(db, 'carriers', carrierId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Carrier;
    } else {
      console.log(`No carrier found with ID: ${carrierId}`);
      return null;
    }
  } catch (error) {
    console.error('Error fetching carrier by ID: ', error);
    throw error;
  }
};

// Save a lane
export const saveLane = async (lane: Lane) => {
  try {
    const laneData = toFirestoreData({
      ...lane,
      createdAt: lane.createdAt || new Date()
    });
    
    if (lane.id) {
      // Update existing lane
      await updateDoc(doc(db, 'lanes', lane.id), laneData);
      return lane.id;
    } else {
      // Add new lane
      const docRef = await addDoc(collection(db, 'lanes'), laneData);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving lane: ', error);
    throw error;
  }
};

// Get all lanes
export const getLanes = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'lanes'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as Lane);
  } catch (error) {
    console.error('Error fetching lanes: ', error);
    throw error;
  }
};

// Save user settings
export const saveSettings = async (settings: Settings) => {
  try {
    const settingsData = toFirestoreData({
      ...settings,
      updatedAt: new Date()
    });
    
    const userSettingsRef = doc(db, 'settings', settings.userId);
    await setDoc(userSettingsRef, settingsData);
    return settings.userId;
  } catch (error) {
    console.error('Error saving settings: ', error);
    throw error;
  }
};

// Get user settings
export const getUserSettings = async (userId: string) => {
  try {
    const docSnap = await getDoc(doc(db, 'settings', userId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Settings;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user settings: ', error);
    throw error;
  }
};

// Save a receipt
export const saveReceipt = async (receipt: Receipt) => {
  try {
    const now = new Date();
    const receiptData = toFirestoreData({
      ...receipt,
      updatedAt: now,
      createdAt: receipt.id ? receipt.createdAt : now
    });
    
    if (receipt.id) {
      // Update existing receipt
      await updateDoc(doc(db, 'receipts', receipt.id), receiptData);
      return receipt.id;
    } else {
      // Add new receipt
      const docRef = await addDoc(collection(db, 'receipts'), receiptData);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving receipt: ', error);
    throw error;
  }
};

// Get receipts for a user
export const getUserReceipts = async (userId: string) => {
  try {
    const q = query(collection(db, 'receipts'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as Receipt);
  } catch (error) {
    console.error('Error fetching user receipts: ', error);
    throw error;
  }
}; 